/**
 * Security Settings Page
 *
 * Allows authenticated users to change their password.
 * Requires active session.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CheckCircle, Lock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/core/auth/auth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  validatePassword,
  getStrengthColor,
  getStrengthBgColor,
} from '@/core/auth/passwordValidation';

export default function SecuritySettings() {
  const navigate = useNavigate();
  const { user, authState } = useAuth();
  const { toast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect if not authenticated
  if (authState !== 'AUTHENTICATED' && authState !== 'GATED') {
    navigate('/login', { replace: true });
    return null;
  }

  const validation = validatePassword(newPassword, user?.email || undefined);
  const passwordsMatch = newPassword === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Re-auth gate: current password must be provided and verified BEFORE
    // we accept the new one. This protects against session-hijack scenarios
    // and replaces the brittle "Supabase sometimes errors on stale session"
    // path the previous code commented around.
    if (!currentPassword) {
      toast({
        variant: 'destructive',
        title: 'Current Password Required',
        description: 'Enter your current password to confirm this change.',
      });
      setLoading(false);
      return;
    }

    // Validate new password
    if (!validation.isValid) {
      toast({
        variant: 'destructive',
        title: 'Invalid Password',
        description: validation.errors[0],
      });
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      toast({
        variant: 'destructive',
        title: 'Passwords Do Not Match',
        description: 'Please ensure both password fields match.',
      });
      setLoading(false);
      return;
    }

    if (!user?.email) {
      toast({
        variant: 'destructive',
        title: 'Session Error',
        description: 'Could not read your account email. Please sign out and back in.',
      });
      setLoading(false);
      return;
    }

    try {
      // Step 1: re-authenticate with the current password to confirm identity
      // and refresh the session before mutating the password. signInWithPassword
      // on an already-authenticated user issues a fresh token pair for the same
      // user without disrupting the session — Supabase's documented re-auth path.
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (reauthError) {
        // Audit-log the failed attempt (PHI-safe)
        try {
          await supabase.from('audit_logs').insert({
            user_id: user.id,
            action: 'PASSWORD_REAUTH_FAILED',
            table_name: 'auth.users',
            metadata: { timestamp: new Date().toISOString() },
          });
        } catch {
          // Silent — audit log failure is non-fatal
        }

        toast({
          variant: 'destructive',
          title: 'Current Password Incorrect',
          description: 'The current password you entered does not match our records.',
        });
        setLoading(false);
        return;
      }

      // Step 2: now that the session is re-authenticated, update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error('[SecuritySettings] Password update error:', updateError);

        if (updateError.message.includes('same as')) {
          toast({
            variant: 'destructive',
            title: 'Password Not Changed',
            description: 'New password must be different from your current password.',
          });
        } else if (updateError.message.includes('reauthentication')) {
          toast({
            variant: 'destructive',
            title: 'Session Expired',
            description: 'Please sign out and sign back in, then try again.',
          });
        } else {
          toast({
            variant: 'destructive',
            title: 'Update Failed',
            description: updateError.message || 'Failed to update password.',
          });
        }
        setLoading(false);
        return;
      }

      // Success
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      toast({
        title: 'Password Updated',
        description: 'Your password has been successfully changed.',
      });

      // Log the event (PHI-safe)
      try {
        await supabase.from('audit_logs').insert({
          user_id: user?.id,
          action: 'PASSWORD_CHANGED',
          table_name: 'auth.users',
          metadata: { timestamp: new Date().toISOString() },
        });
      } catch {
        // Don't fail if audit log fails
      }
    } catch (err) {
      console.error('[SecuritySettings] Unexpected error:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/app"
            className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to App
          </Link>

          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Security Settings</h1>
              <p className="text-muted-foreground">Manage your account security</p>
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className="glass-strong rounded-2xl p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </h2>

          {success ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-green-700 dark:text-green-300">
                  Password changed successfully
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  Your new password is now active.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Enter your current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Required to confirm it&apos;s you before we change the password.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Password strength indicator */}
                {newPassword && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Password strength</span>
                      <span className={getStrengthColor(validation.strength)}>
                        {validation.strength.charAt(0).toUpperCase() + validation.strength.slice(1)}
                      </span>
                    </div>
                    <div className="h-1 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${getStrengthBgColor(validation.strength)}`}
                        style={{ width: `${validation.score}%` }}
                      />
                    </div>
                    {validation.errors.length > 0 && (
                      <p className="text-xs text-destructive">{validation.errors[0]}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={loading || !validation.isValid || !passwordsMatch}
                  className="w-full sm:w-auto"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* Security Tips */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h3 className="font-medium mb-2">Password Tips</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>- Use at least 12 characters</li>
            <li>- Include uppercase, lowercase, numbers, and symbols</li>
            <li>- Avoid common words or personal information</li>
            <li>- Use a unique password for each account</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
